import React, { useContext } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { IInternalTransferTask } from '../../../interfaces/delivery-company/tasks';
import MultiPackageScanner, { validationResult } from '../../common/multi-package-scanner/multi-package-scanner';
import { DecodingWithOrderVal, generateUID, same, transformOrdersToDecodingWithOrderVal } from '../../../utils/encoding';
import { GenericTableProps } from '../../common/generic-table/utils';
import { MerchantWithIcon } from '../../common/styles/merchant-with-icon';
import { CustomerWithIcon } from '../../common/styles/customer-with-icon';
import { showWorkflow } from '../../common/styles/workflow-display';
import { displayLocation } from '../../../utils/locations';
import { DeliveryState } from '../../../enums/orders';
import axios, { AxiosResponse } from 'axios';
import { API_ENDPOINT } from '../../../configurations/global.config';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { InternalTransferTaskState } from '../../../enums/tasks';
import { Typography, Alert } from '@mui/material';

interface ReceiveTransferDialogProps {
  task: IInternalTransferTask;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const ReceiveTransferDialog: React.FC<ReceiveTransferDialogProps> = ({
  task,
  open,
  onClose,
  onSuccess,
}) => {
  const { config } = useContext(AuthContext);

  const completeTransferTask = async (
    orders: DecodingWithOrderVal[],
    setIsLoading: (isLoading: boolean) => void,
    setResponse: (resp?: AxiosResponse) => void,
    setMessage: (m: string) => void,
    config: any
  ) => {
    setIsLoading(true);
    setResponse(undefined);

    try {
      const requestData = {
        status: InternalTransferTaskState.COMPLETED,
      };

      const url = `${API_ENDPOINT}/v1/internal-transfer-task/${task.id}/`;
      const newResponse = await axios.patch(url, requestData, config);
      setResponse(newResponse);
      setMessage('Transfer task completed successfully.');
      
      // Call onSuccess after a short delay to allow the user to see the success message
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setResponse(e.response);
        console.log(e.response);
        setMessage(`Failed to complete task: ${e.response?.statusText}`);
      } else {
        setMessage(`Failed to complete task.`);
      }
    }

    setIsLoading(false);
  };

  const expectedOrders = transformOrdersToDecodingWithOrderVal(task.orders);

  // Check if task is in the correct state to be received
  const canReceive = 
    task.status === InternalTransferTaskState.COLLECTED ||
    task.status === InternalTransferTaskState.IN_PROGRESS || 
    task.status === InternalTransferTaskState.ASSIGNED;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Receive Transfer Task #{task.id}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary">
          From: {task.source_branch.name} → To: {task.target_branch.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Status: {task.status}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {!canReceive && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This transfer cannot be received yet. The driver must first collect the packages from the source branch.
            (Current status: {task.status})
          </Alert>
        )}
        <MultiPackageScanner
          label="Scan package ID"
          disabled={!canReceive}
          validator={(decodingWithVal: DecodingWithOrderVal): validationResult => {
            
            // Check if package belongs to this transfer task
            if (!expectedOrders.some((item) => same(decodingWithVal, item))) {
              return {
                validated: false,
                error: 'Package does not belong to this transfer task',
              };
            }

            return { validated: true };
          }}
          callback={completeTransferTask}
          allowUndoScan={true}
          actionType="Receive Transfer"
          expectedOrders={expectedOrders}
          getRenderOrdersProps={(
            orders: DecodingWithOrderVal[]
          ): GenericTableProps<DecodingWithOrderVal> => {
            return {
              sourceView: 'receive-transfer-dialog',
              items: orders,
              fullWidth: true,
              columns: [
                {
                  id: 'order_id',
                  header: 'Order ID',
                  display_body: (decoding) => <>{decoding.id}</>,
                },
                {
                  id: 'merchant',
                  header: 'Merchant',
                  display_body: (decoding) => (
                    <Box>
                      <MerchantWithIcon merchant={decoding.order.merchant} centered />
                      {decoding.order.merchant_location && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}
                        >
                          {displayLocation(decoding.order.merchant_location)}
                        </Typography>
                      )}
                    </Box>
                  ),
                },
                {
                  id: 'customer',
                  header: 'Customer',
                  display_body: (decoding) => (
                    <Box>
                      <CustomerWithIcon customer={decoding.order.customer} centered />
                      {decoding.order.customer_location && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}
                        >
                          {displayLocation(decoding.order.customer_location)}
                        </Typography>
                      )}
                    </Box>
                  ),
                },
                {
                  id: 'workflow',
                  header: 'Workflow',
                  display_body: (decoding) => (
                    <>
                      {showWorkflow(
                        decoding.order.workflow,
                        decoding.order.is_exchange,
                        decoding.order.exchange_for,
                        decoding.order.return_reason
                      )}
                    </>
                  ),
                },
              ],
              getItemUID: (decoding) => generateUID(decoding),
              selectedItems: new Map(),
              isLoading: false,
            };
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

